import { VFC } from 'react';
import { Link } from 'react-router-dom-v5-compat';

import AttributeItem, { AttributeGrid } from 'dashboard/components/AttributeItem';
import { WorkflowRunStepKnownObjects } from 'dashboard/models/workflow-step';
import { ApiModel } from 'lib/interfaces/api';
import { assertNever } from 'lib/utils/types';

interface ResultObjectProps {
  object: WorkflowRunStepKnownObjects;
}

const ResultObject: VFC<ResultObjectProps> = ({ object }) => {
  const [idTitle, idLink] = (() => {
    switch (object.type) {
      case ApiModel.Inquiry: {
        return ['Inquiry ID', `/dashboard/inquiries/${object.id}`];
      }
      case ApiModel.Account: {
        return ['Account ID', `/dashboard/accounts/${object.id}`];
      }

      case ApiModel.Case: {
        return ['Case ID', `/dashboard/cases/${object.id}`];
      }

      case ApiModel.CaseMessage: {
        return ['Case Message ID', `/dashboard/cases/${object.relationships.case.data.id}`];
      }

      case ApiModel.GraphQuery: {
        return ['Graph Query ID', `/dashboard/explorer?queryId=${object.id}`];
      }

      case ApiModel.ListItemBrowserFingerprint:
      case ApiModel.ListItemCountry:
      case ApiModel.ListItemDeviceFingerprint:
      case ApiModel.ListItemEmailAddress:
      case ApiModel.ListItemFace:
      case ApiModel.ListItemField:
      case ApiModel.ListItemGeolocation:
      case ApiModel.ListItemGovernmentIdNumber:
      case ApiModel.ListItemIpAddress:
      case ApiModel.ListItemName:
      case ApiModel.ListItemPhoneNumber:
      case ApiModel.ListItemString: {
        return ['List Item ID', `/dashboard/lists/${object.attributes['list-id']}`];
      }

      case ApiModel.ReportAddressLookup:
      case ApiModel.ReportAdverseMedia:
      case ApiModel.ReportBusinessAdverseMedia:
      case ApiModel.ReportBusinessAssociatedPersons:
      case ApiModel.ReportBusinessClassification:
      case ApiModel.ReportBusinessIndustryClassification:
      case ApiModel.ReportBusinessLiensLookup:
      case ApiModel.ReportBusinessLookup:
      case ApiModel.ReportBusinessNonprofit:
      case ApiModel.ReportBusinessOnlinePresence:
      case ApiModel.ReportBusinessRegistrationsLookup:
      case ApiModel.ReportBusinessSocialMedia:
      case ApiModel.ReportBusinessWatchlist:
      case ApiModel.ReportBusinessWebsite:
      case ApiModel.ReportChainalysisAddressScreening:
      case ApiModel.ReportClearbitBusinessLookup:
      case ApiModel.ReportCryptoAddressWatchlist:
      case ApiModel.ReportCustomList:
      case ApiModel.ReportEmailAddress:
      case ApiModel.ReportEquifaxOneview:
      case ApiModel.ReportFinraBrokerCheck:
      case ApiModel.ReportKyckrBusinessLookup:
      case ApiModel.ReportMiddeskBusinessLookup:
      case ApiModel.ReportMxAccount:
      case ApiModel.ReportPhoneNumber:
      case ApiModel.ReportPoliticallyExposedPerson:
      case ApiModel.ReportProfile:
      case ApiModel.ReportSecActionLookup:
      case ApiModel.ReportSentilinkApplicationRisk:
      case ApiModel.ReportSentilinkScores:
      case ApiModel.ReportSocialMedia:
      case ApiModel.ReportSynthetic:
      case ApiModel.ReportTrmWalletScreening:
      case ApiModel.ReportWatchlist: {
        return ['Report ID', `/dashboard/reports/${object.id}`];
      }

      case ApiModel.SAR: {
        return ['SAR ID', `/dashboard/sars/${object.id}`];
      }

      case ApiModel.Transaction: {
        return ['Transaction ID', `/dashboard/transactions/${object.id}`];
      }

      case ApiModel.VerificationAamva:
      case ApiModel.VerificationBusinessWebsite:
      case ApiModel.VerificationCertificateKorea:
      case ApiModel.VerificationDatabase:
      case ApiModel.VerificationDatabaseAadhaar:
      case ApiModel.VerificationDatabaseBusiness:
      case ApiModel.VerificationDatabaseBusinessFootprint:
      case ApiModel.VerificationDatabaseStandard:
      case ApiModel.VerificationDatabaseEcbsv:
      case ApiModel.VerificationDatabasePhoneCarrier:
      case ApiModel.VerificationDatabaseSerpro:
      case ApiModel.VerificationDatabaseTin:
      case ApiModel.VerificationDatabaseVatNumber:
      case ApiModel.VerificationDigitalIdFinnishTrustNetwork:
      case ApiModel.VerificationDigitalIdOneId:
      case ApiModel.VerificationDigitalIdSerpro:
      case ApiModel.VerificationDigitalIdSingpass:
      case ApiModel.VerificationDigitalIdUkSharecode:
      case ApiModel.VerificationDocument:
      case ApiModel.VerificationEmailAddress:
      case ApiModel.VerificationGovernmentId:
      case ApiModel.VerificationGovernmentIdNfc:
      case ApiModel.VerificationMobileDriversLicense:
      case ApiModel.VerificationPhoneNumber:
      case ApiModel.VerificationSelfie: {
        return ['Verification ID', `/dashboard/verifications/${object.id}`];
      }

      // TODO: Create and link out to document view page
      case ApiModel.DocumentGovernmentId: {
        return [null, null];
      }

      // TODO[WOR-1421]: Create report view
      case ApiModel.ReportCoinbaseCheckCryptoRisk: {
        return [null, null];
      }

      default: {
        assertNever(object);
      }
    }
  })();

  // TODO[WOR-1421]: Create report view
  if (idLink == null) {
    return null;
  }

  return (
    <AttributeGrid colCount={1}>
      <AttributeItem title={idTitle} value={<Link to={idLink}>{object.id}</Link>} />
    </AttributeGrid>
  );
};

export default ResultObject;
